import { useEffect, useMemo, useState } from 'react';
import {
  matchProductNames,
  type ProductNameMatchItem,
  type ProductNameMissReason,
} from '@/api/product';
import {
  UNLIMITED_SPEC_LABEL,
  type OrderItem,
} from '@/pages/order/components/OrderItemsTable';
import { isOutsideFlag } from '@/api/product';

export type PasteMatchFailReason = ProductNameMissReason;

export interface PasteCodeLine {
  title: string;
  name: string;
  quantity: number;
  remark: string;
  matched?: ProductNameMatchItem;
  /** 不限定规格（仅商品匹配） */
  unlimitedSpec?: boolean;
  /** 未匹配原因：找不到物资 / 规格无法匹配 */
  matchFail?: PasteMatchFailReason;
}

/** 未匹配行（含原始粘贴行号） */
export interface PasteMissedItem {
  line: number;
  title: string;
  name: string;
  reason?: PasteMatchFailReason;
}

const REMARK_MAX = 200;

export const PASTE_MATCH_FAIL_LABEL: Record<PasteMatchFailReason, string> = {
  goods: '无法匹配物资',
  spec: '无法匹配规格',
};

export function composePasteRemark(line: Pick<PasteCodeLine, 'remark' | 'matchFail'>): string {
  const extra = String(line.remark || '').trim();
  if (!line.matchFail) return extra;
  const fail = PASTE_MATCH_FAIL_LABEL[line.matchFail];
  if (!extra) return fail;
  return `${fail}；${extra}`.slice(0, REMARK_MAX);
}

/** 用户重新选择物资后，去掉自动写入的匹配失败提示，保留原备注 */
export function stripPasteMatchFailRemark(remark?: string): string {
  const text = String(remark || '').trim();
  if (!text) return '';
  if (text === PASTE_MATCH_FAIL_LABEL.goods || text === PASTE_MATCH_FAIL_LABEL.spec) return '';
  const prefixes = [PASTE_MATCH_FAIL_LABEL.goods, PASTE_MATCH_FAIL_LABEL.spec];
  for (const prefix of prefixes) {
    if (text.startsWith(`${prefix}；`)) return text.slice(prefix.length + 1);
  }
  return text;
}

/** 将粘贴匹配行转为清单行；未匹配时仍保留名称/规格，备注写入失败原因 */
export function pasteLineToOrderItem(line: PasteCodeLine): OrderItem {
  if (line.matchFail || !line.matched) {
    return {
      productId: '',
      name: line.title || '未命名物资',
      quantity: line.quantity,
      unitPrice: 0,
      unit: '件',
      specId: undefined,
      spec: line.unlimitedSpec ? UNLIMITED_SPEC_LABEL : line.name || '',
      image: '',
      itemRemark: composePasteRemark({
        remark: line.remark,
        matchFail: line.matchFail || 'goods',
      }),
      specOptions: [],
    };
  }

  const rawSpecId = Number(line.matched.id);
  const hasSpec = Number.isFinite(rawSpecId) && rawSpecId > 0 && !line.unlimitedSpec;
  const specId = hasSpec ? String(line.matched.id) : '';
  const goodsId = String(line.matched.goods_id || line.matched.goods?.id || '');
  const name = String(line.matched.goods?.title || line.matched.title || '未命名物资');
  const specName = hasSpec
    ? String(line.matched.spec_name || line.matched.name || '')
    : UNLIMITED_SPEC_LABEL;
  const price = Number(line.matched.price ?? 0) || 0;
  const unit = String(line.matched.unit || '件');
  const image = String(line.matched.goods?.m_pic || line.matched.pic || '');

  return {
    productId: goodsId,
    name,
    quantity: line.quantity,
    unitPrice: price,
    unit,
    specId: hasSpec ? specId : undefined,
    spec: specName,
    image,
    itemRemark: line.remark.trim() || '',
    specOptions: hasSpec
      ? [
          {
            id: specId,
            name: specName,
            price,
            unit,
            isOutside: isOutsideFlag(line.matched.is_outside),
          },
        ]
      : [],
    isOutside: hasSpec ? isOutsideFlag(line.matched.is_outside) : false,
  };
}

interface PasteCodesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (lines: PasteCodeLine[], missed: PasteMissedItem[]) => void;
  /** 是否展示「不限定规格」（采购计划按商品总量限购） */
  allowUnlimitedSpec?: boolean;
}

function toLines(text: string): string[] {
  return String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n');
}

function parseQuantity(raw: string): number | null {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return 1;
  const n = Math.floor(Number(trimmed));
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

function matchKey(title: string, name: string, unlimited: boolean) {
  return unlimited ? `g\0${title}` : `${title}\0${name}`;
}

function formatMissed(missed: PasteMissedItem[], unlimited: boolean) {
  return missed
    .map((item) =>
      unlimited || !item.name
        ? `第${item.line}行「${item.title}」`
        : `第${item.line}行「${item.title} / ${item.name}」`,
    )
    .join('、');
}

export default function PasteCodesModal({
  isOpen,
  onClose,
  onConfirm,
  allowUnlimitedSpec = false,
}: PasteCodesModalProps) {
  const [titleText, setTitleText] = useState('');
  const [nameText, setNameText] = useState('');
  const [qtyText, setQtyText] = useState('');
  const [remarkText, setRemarkText] = useState('');
  const [unlimitedSpec, setUnlimitedSpec] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [missed, setMissed] = useState<PasteMissedItem[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setTitleText('');
      setNameText('');
      setQtyText('');
      setRemarkText('');
      setUnlimitedSpec(false);
      setLoading(false);
      setError('');
      setMissed([]);
    }
  }, [isOpen]);

  const titleLines = useMemo(() => toLines(titleText), [titleText]);
  const nameLines = useMemo(() => toLines(nameText), [nameText]);
  const qtyLines = useMemo(() => toLines(qtyText), [qtyText]);
  const remarkLines = useMemo(() => toLines(remarkText), [remarkText]);

  const pairRows = useMemo(() => {
    if (unlimitedSpec) {
      return titleLines
        .map((value, index) => ({
          line: index + 1,
          title: value.trim(),
          name: '',
        }))
        .filter((row) => row.title.length > 0);
    }
    const maxLen = Math.max(titleLines.length, nameLines.length);
    const rows: Array<{ line: number; title: string; name: string }> = [];
    for (let i = 0; i < maxLen; i++) {
      const title = (titleLines[i] ?? '').trim();
      const name = (nameLines[i] ?? '').trim();
      if (!title && !name) continue;
      rows.push({ line: i + 1, title, name });
    }
    return rows;
  }, [titleLines, nameLines, unlimitedSpec]);

  const filledTitleCount = useMemo(
    () => titleLines.filter((v) => v.trim().length > 0).length,
    [titleLines],
  );
  const filledNameCount = useMemo(
    () => nameLines.filter((v) => v.trim().length > 0).length,
    [nameLines],
  );
  const filledQtyCount = useMemo(
    () => qtyLines.filter((v) => v.trim().length > 0).length,
    [qtyLines],
  );
  const filledRemarkCount = useMemo(
    () => remarkLines.filter((v) => v.trim().length > 0).length,
    [remarkLines],
  );

  const preview = useMemo(() => {
    return pairRows.map((row) => {
      const qtyRaw = qtyLines[row.line - 1] ?? '';
      const remarkRaw = (remarkLines[row.line - 1] ?? '').trim().slice(0, REMARK_MAX);
      return {
        line: row.line,
        title: row.title,
        name: row.name,
        quantity: parseQuantity(qtyRaw),
        qtyRaw: qtyRaw.trim(),
        remark: remarkRaw,
      };
    });
  }, [pairRows, qtyLines, remarkLines]);

  const handleConfirm = async () => {
    if (loading) return;
    setError('');
    setMissed([]);

    if (pairRows.length === 0) {
      setError(
        unlimitedSpec
          ? '请粘贴物资名称（每行一条）'
          : '请粘贴物资名称与规格型号（每行一条，按行对应）',
      );
      return;
    }

    const pairs: Array<{
      line: number;
      title: string;
      name: string;
      quantity: number;
      remark: string;
    }> = [];

    for (const row of pairRows) {
      if (!row.title) {
        setError(`第 ${row.line} 行请填写物资名称`);
        return;
      }
      if (!unlimitedSpec && !row.name) {
        setError(`第 ${row.line} 行请填写规格型号`);
        return;
      }
      const qtyRaw = qtyLines[row.line - 1] ?? '';
      const quantity = parseQuantity(qtyRaw);
      if (quantity == null) {
        setError(`第 ${row.line} 行数量无效：${qtyRaw.trim() || '(空)'}`);
        return;
      }
      const remark = (remarkLines[row.line - 1] ?? '').trim().slice(0, REMARK_MAX);
      pairs.push({
        line: row.line,
        title: row.title,
        name: unlimitedSpec ? '' : row.name,
        quantity,
        remark,
      });
    }

    const uniqueItems = Array.from(
      new Map(
        pairs.map((p) => [
          matchKey(p.title, p.name, unlimitedSpec),
          { title: p.title, name: p.name },
        ]),
      ).values(),
    );

    setLoading(true);
    try {
      const res = await matchProductNames(uniqueItems);
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setError(res.message || res.msg || '批量匹配失败');
        return;
      }

      const matchedList = Array.isArray(res.data?.matched) ? res.data!.matched! : [];
      const missedFromApi = Array.isArray(res.data?.missed) ? res.data!.missed! : [];
      const byKey = new Map(
        matchedList.map((item) => [
          matchKey(String(item.title), String(item.name || ''), unlimitedSpec),
          item,
        ]),
      );
      const reasonByKey = new Map<string, PasteMatchFailReason>();
      missedFromApi.forEach((item) => {
        const key = matchKey(String(item.title), String(item.name || ''), unlimitedSpec);
        if (item.reason === 'spec' || item.reason === 'goods') {
          reasonByKey.set(key, item.reason);
        }
      });

      const unclassified = pairs.filter((p) => {
        const key = matchKey(p.title, p.name, unlimitedSpec);
        if (byKey.has(key) || reasonByKey.has(key)) return false;
        return !unlimitedSpec && !!p.name;
      });
      if (unclassified.length > 0) {
        const uniqueTitles = Array.from(new Set(unclassified.map((p) => p.title)));
        const goodsRes = await matchProductNames(uniqueTitles.map((title) => ({ title, name: '' })));
        const goodsOk = goodsRes.error === false || Number(goodsRes.error) === 0;
        const goodsMatchedTitles = new Set(
          goodsOk && Array.isArray(goodsRes.data?.matched)
            ? goodsRes.data!.matched!.map((item) => String(item.title))
            : [],
        );
        unclassified.forEach((p) => {
          const key = matchKey(p.title, p.name, unlimitedSpec);
          reasonByKey.set(key, goodsMatchedTitles.has(p.title) ? 'spec' : 'goods');
        });
      }

      const lines: PasteCodeLine[] = pairs.map((pair) => {
        const key = matchKey(pair.title, pair.name, unlimitedSpec);
        const hit = byKey.get(key);
        if (hit) {
          return {
            title: pair.title,
            name: pair.name,
            quantity: pair.quantity,
            remark: pair.remark,
            matched: hit,
            unlimitedSpec,
          };
        }
        const matchFail: PasteMatchFailReason = reasonByKey.get(key) || 'goods';
        return {
          title: pair.title,
          name: pair.name,
          quantity: pair.quantity,
          remark: pair.remark,
          unlimitedSpec,
          matchFail,
        };
      });

      const missedItems: PasteMissedItem[] = [];
      pairs.forEach((pair) => {
        const key = matchKey(pair.title, pair.name, unlimitedSpec);
        if (byKey.has(key)) return;
        missedItems.push({
          line: pair.line,
          title: pair.title,
          name: pair.name,
          reason: reasonByKey.get(key) || 'goods',
        });
      });

      setMissed(missedItems);
      onConfirm(lines, missedItems);
      onClose();
    } catch {
      setError('网络异常，批量匹配失败');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-background-50 rounded-xl w-full max-w-6xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground-900">批量匹配物资</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              {unlimitedSpec
                ? '按行对应填写物资名称、数量、备注（不限定规格；数量/备注可留空；数量默认 1）'
                : '按行对应填写物资名称、规格型号、数量、备注（数量/备注可留空；数量默认 1）'}
              ；找不到物资或规格无法匹配时仍会加入清单，并在物资备注中标注原因
            </p>
          </div>
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors disabled:opacity-50"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-4 gap-3">
            <div className="min-w-0">
              <label className="text-xs font-medium text-foreground-700 mb-1.5 flex items-center gap-1">
                <i className="ri-price-tag-3-line text-foreground-400" />
                物资名称
                <span className="text-foreground-400 font-normal">（{filledTitleCount} 行）</span>
              </label>
              <textarea
                value={titleText}
                disabled={loading}
                onChange={(e) => setTitleText(e.target.value)}
                rows={12}
                placeholder={'例如：\n一次性注射器\n医用纱布\n输液器'}
                className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm font-mono outline-none resize-none transition-colors focus:border-primary-400 disabled:bg-background-100 leading-6"
              />
            </div>

            <div className="min-w-0">
              <div className="mb-1.5 flex items-center justify-between gap-2 min-h-[1.25rem]">
                <label className="text-xs font-medium text-foreground-700 flex items-center gap-1 min-w-0">
                  <i className="ri-file-list-2-line text-foreground-400" />
                  规格型号
                  <span className="text-foreground-400 font-normal">（{filledNameCount} 行）</span>
                </label>
                {allowUnlimitedSpec && (
                  <label className="inline-flex items-center gap-1 text-[11px] text-foreground-600 cursor-pointer select-none shrink-0 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={unlimitedSpec}
                      disabled={loading}
                      onChange={(e) => {
                        setUnlimitedSpec(e.target.checked);
                        setError('');
                        setMissed([]);
                      }}
                      className="rounded border-background-300 text-primary-500 focus:ring-primary-400"
                    />
                    不限定规格
                  </label>
                )}
              </div>
              {!unlimitedSpec ? (
                <textarea
                  value={nameText}
                  disabled={loading}
                  onChange={(e) => setNameText(e.target.value)}
                  rows={12}
                  placeholder={'例如：\n5ml\n10cm×10cm\n一次性'}
                  className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm font-mono outline-none resize-none transition-colors focus:border-primary-400 disabled:bg-background-100 leading-6"
                />
              ) : (
                <div className="h-[calc(12*1.5rem+1.25rem)] rounded-lg border border-dashed border-accent-200 bg-accent-50/60 px-3 py-3 text-xs text-accent-800 flex flex-col gap-1.5">
                  <span className="font-medium">已启用不限定规格</span>
                  <span className="text-accent-700/80 leading-relaxed">
                    仅按物资名称匹配商品，提交时不指定规格 ID，按商品总量限购。
                  </span>
                </div>
              )}
            </div>

            <div className="min-w-0">
              <label className="text-xs font-medium text-foreground-700 mb-1.5 flex items-center gap-1">
                <i className="ri-hashtag text-foreground-400" />
                采购数量
                <span className="text-foreground-400 font-normal">（{filledQtyCount} 行）</span>
              </label>
              <textarea
                value={qtyText}
                disabled={loading}
                onChange={(e) => setQtyText(e.target.value)}
                rows={12}
                placeholder={'例如：\n10\n5\n20'}
                className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm font-mono outline-none resize-none transition-colors focus:border-primary-400 disabled:bg-background-100 leading-6"
              />
            </div>
            <div className="min-w-0">
              <label className="text-xs font-medium text-foreground-700 mb-1.5 flex items-center gap-1">
                <i className="ri-chat-1-line text-foreground-400" />
                物资备注
                <span className="text-foreground-400 font-normal">（{filledRemarkCount} 行）</span>
              </label>
              <textarea
                value={remarkText}
                disabled={loading}
                onChange={(e) => setRemarkText(e.target.value)}
                rows={12}
                placeholder={'例如：\n加急\n备货\n'}
                className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm font-mono outline-none resize-none transition-colors focus:border-primary-400 disabled:bg-background-100 leading-6"
              />
            </div>
          </div>

          {preview.length > 0 && (
            <div className="rounded-lg border border-background-200 bg-white max-h-36 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-background-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium text-foreground-500">行</th>
                    <th className="text-left px-3 py-1.5 font-medium text-foreground-500">物资名称</th>
                    {!unlimitedSpec && (
                      <th className="text-left px-3 py-1.5 font-medium text-foreground-500">
                        规格型号
                      </th>
                    )}
                    <th className="text-right px-3 py-1.5 font-medium text-foreground-500">数量</th>
                    <th className="text-left px-3 py-1.5 font-medium text-foreground-500">备注</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.slice(0, 50).map((row) => (
                    <tr
                      key={`${row.line}-${row.title}-${row.name}`}
                      className="border-t border-background-100"
                    >
                      <td className="px-3 py-1 text-foreground-400">{row.line}</td>
                      <td className="px-3 py-1 text-foreground-800">
                        {row.title || <span className="text-red-500">缺</span>}
                      </td>
                      {!unlimitedSpec && (
                        <td className="px-3 py-1 text-foreground-800">
                          {row.name || <span className="text-red-500">缺</span>}
                        </td>
                      )}
                      <td
                        className={`px-3 py-1 text-right font-mono ${
                          row.quantity == null ? 'text-red-500' : 'text-foreground-700'
                        }`}
                      >
                        {row.qtyRaw
                          ? row.quantity == null
                            ? row.qtyRaw
                            : row.quantity
                          : '1（默认）'}
                      </td>
                      <td className="px-3 py-1 text-foreground-600 max-w-[12rem] truncate">
                        {row.remark || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.length > 50 && (
                <p className="px-3 py-1.5 text-[11px] text-foreground-400 border-t border-background-100">
                  仅预览前 50 行，共 {preview.length} 行
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-xs text-red-600 flex items-start gap-1.5 max-h-40 overflow-y-auto">
              <i className="ri-error-warning-line shrink-0 mt-0.5" />
              <span className="break-all min-w-0 flex-1">{error}</span>
            </div>
          )}
          {!error && missed.length > 0 && (
            <div className="px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-700 flex items-start gap-1.5 max-h-40 overflow-y-auto">
              <i className="ri-information-line shrink-0 mt-0.5" />
              <span className="break-all min-w-0 flex-1">
                找不到物资：{formatMissed(missed, unlimitedSpec)}
              </span>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-background-200 flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            disabled={loading || pairRows.length === 0}
            onClick={() => void handleConfirm()}
            className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-60 inline-flex items-center gap-1.5"
          >
            {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-file-search-line" />}
            {loading ? '匹配中...' : '匹配并加入清单'}
          </button>
        </div>
      </div>
    </div>
  );
}
